import type { MockDatabase, MockRecord } from './database.ts';
import { seedDatabase, seedId } from './seed.ts';
import { applyLegacyVendorUsers } from './legacyVendorUsers.ts';

const examples = [
  { vendor: 'ENROIL OILFIELD SOLUTIONS AND SERVICES LLC', product: 'BDF(TM)-918, OM', lab: 'Muscat', date: '2026-10-07', manager: 'Vladimir Gankin' },
  { vendor: 'OPTIMIZE DRILLING FLUIDS', product: 'LIME', lab: 'Cairo', date: '2026-10-06', manager: 'Ayten Rady' },
  { vendor: 'OPTIMIZE DRILLING FLUIDS', product: 'SODIUM CHLORIDE 99% COARSE', lab: 'Cairo', date: '2026-10-06', manager: 'Ayten Rady' },
  { vendor: 'Juhua Group (Hongkong) Limited', product: 'CALCIUM CHLORIDE; ANHYDROUS POWDER', lab: 'Pune', date: '2026-09-30', manager: 'Peter Vitale' },
  { vendor: 'PACIFIC COAST RESOURCES CORPORATION', product: 'POTASSIUM CHLORIDE', lab: 'Houston', date: '2026-09-30', manager: 'Peter Vitale' },
];

export function seedBusinessDemoDatabase(): MockDatabase {
  const database = seedDatabase();
  const vendorNames = [...new Set(examples.map((example) => example.vendor)), 'Prototype Packaging Company'];
  const vendors = database.vendors.filter((vendor) => vendor.isApproved).slice(0, 5);
  const vendorIds = new Set(vendors.map((vendor) => vendor.oid));
  const namesByVendorId = new Map(vendors.map((vendor, index) => [vendor.oid, vendorNames[index]]));
  const contacts = new Set<string>();
  for (const vendor of vendors) {
    for (const contact of database.users.filter((user) => user.isUser && user.vendorId === vendor.oid).slice(0, 2)) contacts.add(contact.oid);
  }
  database.users = database.users.filter((user) => user.portal !== 'QCDOC-VENDOR' || vendorIds.has(user.oid) || contacts.has(user.oid));
  database.vendors = vendors;
  for (const user of database.users.filter((user) => user.portal === 'QCDOC-VENDOR')) {
    const company = namesByVendorId.get(String(user.isUser ? user.vendorId : user.oid));
    Object.assign(user, { company, vendorName: company, name: company });
  }

  const products = [...database.products.filter((product) => product.status === 11).slice(0, 5),
    ...database.products.filter((product) => product.status === 10).slice(0, 2),
    ...database.products.filter((product) => product.status === 12).slice(0, 1)];
  const productIds = new Set(products.map((product) => product.oid));
  const exampleByProductId = new Map(products.slice(0, 5).map((product, index) => [product.oid, examples[index]]));
  for (const product of products) {
    const example = exampleByProductId.get(product.oid);
    if (example) product.name = example.product;
    product.notes = 'Prototype fixture: workflow details and specifications are synthetic.';
  }
  database.products = products;
  database.specs = database.specs.filter((spec) => productIds.has(String(spec.productId)));
  const stpIds = new Set(database.specs.map((spec) => String(spec.stpId)));
  database.stps = [...database.stps.filter((stp) => stpIds.has(stp.oid)),
    ...database.stps.filter((stp) => !stpIds.has(stp.oid)).slice(0, 5 - stpIds.size)];

  const selectWorkflow = (table: string, count = 8): MockRecord[] => {
    const candidates = database[table].filter((row) => exampleByProductId.has(String(row.productId)));
    const states = [...new Set(candidates.map((row) => `${row.status}:${!!row.deleted}`))];
    const selected = states.flatMap((state) => candidates.find((row) => `${row.status}:${!!row.deleted}` === state) ?? []).slice(0, count);
    for (const productId of exampleByProductId.keys()) {
      if (selected.length === count) break;
      const row = candidates.find((candidate) => candidate.productId === productId && (table !== 'coas' || candidate.status === 3 && !candidate.deleted))
        ?? candidates.find((candidate) => candidate.productId === productId);
      if (row && !selected.some((candidate) => candidate.productId === productId)) selected.push(row);
    }
    for (const row of candidates) {
      if (selected.length === count) break;
      if (!selected.includes(row)) selected.push(row);
    }
    return selected;
  };
  const approved = database.vqcas.filter((row) => row.status === 5 && exampleByProductId.has(String(row.productId))).slice(0, 5);
  database.vqcas = [...approved, ...selectWorkflow('vqcas').filter((row) => row.status !== 5).slice(0, 3)];
  for (const table of ['vendorRequests', 'coas', 'samples', 'coqs', 'packages', 'shipments']) {
    database[table] = selectWorkflow(table, table === 'coas' || table === 'samples' ? 10 : 8);
  }

  const labels = (row: MockRecord) => {
    const example = exampleByProductId.get(String(row.productId ?? row.prodId));
    if (!example) return;
    const vendor = vendors.find((entry) => entry.company === example.vendor)!;
    Object.assign(row, { vendorId: vendor.oid, company: example.vendor, vendorName: example.vendor,
      vendorCompany: example.vendor, venCompany: example.vendor, productName: example.product, prodName: example.product,
      vendorProductName: example.product, notes: 'Prototype fixture: linked workflow details are synthetic.' });
  };
  for (const table of ['suppliers', 'formulas', 'productApprovals']) {
    database[table] = database[table].filter((row) => productIds.has(String(row.productId ?? row.prodId)));
  }
  for (const table of ['vqcas', 'vendorRequests', 'coas', 'samples', 'coqs', 'packages', 'shipments', 'suppliers', 'formulas']) {
    database[table].forEach(labels);
  }
  for (const agreement of approved) {
    const example = exampleByProductId.get(String(agreement.productId))!;
    const manager = example.manager.split(' ');
    Object.assign(agreement, { catName: 'VQCA Level 1', qaLab: example.lab,
      issueDate: `${example.date}T10:00:00.000Z`, catFName: manager[0], catLName: manager.slice(1).join(' ') });
  }
  for (const coa of database.coas) coa.vqcaId = approved.find((row) => row.productId === coa.productId)!.oid;
  for (const coq of database.coqs) {
    const coa = database.coas.find((row) => row.productId === coq.productId && row.status === 3 && !row.deleted)
      ?? database.coas.find((row) => row.productId === coq.productId)!;
    coq.coaId = coa.oid;
    coq.lotNumber = coa.lotNumber;
  }
  for (const shipment of database.shipments) shipment.coaId = database.coas.find((row) => row.productId === shipment.productId)?.oid ?? null;
  const pruneChildren = (table: string, field: string, parents: string) => {
    const ids = new Set(database[parents].map((row) => row.oid));
    database[table] = database[table].filter((row) => ids.has(String(row[field])));
  };
  for (const table of ['vqcaSpecs', 'signLogs']) pruneChildren(table, 'vqcaId', 'vqcas');
  for (const table of ['coaPos', 'coaExperiments', 'coaResults']) pruneChildren(table, 'coaId', 'coas');
  const reportTemplate = database.qaReports[0];
  pruneChildren('experiments', 'sampleId', 'samples');
  database.qaReports = database.samples.map((sample, index) => ({ ...reportTemplate, oid: seedId(42, index),
    sampleId: sample.oid, sampleOid: sample.oid, qaNumber: sample.qaNumber, qaReportNumber: 600001 + index,
    qaReportId: 600001 + index, lotNumber: sample.lotNumber, product: sample.prodName, vendor: sample.vendorCompany,
    pslId: sample.pslId, pslName: sample.psl, dateReceived: sample.dateRcvd, status: sample.statusName,
    expStatus: sample.statusName, isApproved: sample.status === 1, isTestApproved: sample.status === 1 }));
  for (const table of ['packageLots', 'packagePos']) pruneChildren(table, 'pkgSlipId', 'packages');
  pruneChildren('coqPos', 'coqId', 'coqs');
  for (const report of database.qaReports) {
    const sample = database.samples.find((row) => row.oid === report.sampleId)!;
    Object.assign(report, { product: sample.prodName, vendor: sample.vendorCompany });
  }
  for (const row of database.packageLots) {
    const parent = database.packages.find((record) => record.oid === row.pkgSlipId)!;
    const coa = database.coas.find((record) => record.productId === parent.productId);
    Object.assign(row, { vendorId: parent.vendorId, lotNumber: coa?.lotNumber ?? row.lotNumber });
  }
  for (const row of database.packages) Object.assign(row, { packagerName: vendors[0].company, packagerCompany: vendors[0].company, packager: vendors[0].company });
  const coaIds = new Set(database.coas.map((row) => row.oid));
  const qaNumbers = new Set(database.samples.map((row) => row.qaNumber));
  database.attachments = database.attachments.filter((row) => coaIds.has(String(row.coaId)) || qaNumbers.has(row.qanumber));
  database.packagerVendors = vendors.map((vendor, index) => ({ oid: seedId(63, index), packagerId: vendors[0].oid,
    vendorId: vendor.oid, vendorCompany: vendor.company, packagerCompany: vendors[0].company,
    productCount: approved.filter((row) => row.vendorId === vendor.oid).length,
    assignedProductNames: approved.filter((row) => row.vendorId === vendor.oid).map((row) => row.prodName) }));
  database.packagerProducts = approved.map((row, index) => ({ oid: seedId(64, index), packagerId: vendors[0].oid,
    vendorId: row.vendorId, productId: row.productId, prodName: row.prodName }));
  for (const table of ['vendorsInRoles', 'vendorUsersInRoles']) {
    const userIds = new Set(database.users.map((user) => user.oid));
    database[table] = database[table].filter((row) => userIds.has(String(row.userId)));
  }
  database.businessDemoSeed = [{ oid: 'compact-business-demo-1', details: 'Screenshot names; synthetic linked workflow data.' }];
  applyLegacyVendorUsers(database);
  return database;
}

export function applyBusinessDemoCleanup(database: MockDatabase): boolean {
  if (database.businessDemoSeed?.some((row) => row.oid === 'compact-business-demo-1')) return false;
  const compact = seedBusinessDemoDatabase();
  const tables = ['vendors', 'products', 'stps', 'specs', 'formulas', 'suppliers', 'productApprovals',
    'vqcas', 'vqcaSpecs', 'signLogs', 'vendorRequests', 'coas', 'coaPos', 'coaExperiments', 'coaResults',
    'samples', 'experiments', 'qaReports', 'coqs', 'coqPos', 'packages', 'packageLots', 'packagePos',
    'shipments', 'attachments', 'packagerVendors', 'packagerProducts', 'vendorsInRoles', 'vendorUsersInRoles', 'integrityEvents'];
  const archived = Object.fromEntries(tables.map((table) => [table, structuredClone(database[table] ?? [])]));
  archived.vendorAccounts = structuredClone(database.users.filter((user) => user.portal === 'QCDOC-VENDOR'));
  (database.businessDemoArchive ??= []).push({ oid: 'before-compact-business-demo-1',
    archivedOn: new Date().toISOString(), tables: archived });
  for (const table of tables) database[table] = compact[table] ?? [];
  database.users = [...database.users.filter((user) => user.portal !== 'QCDOC-VENDOR'),
    ...compact.users.filter((user) => user.portal === 'QCDOC-VENDOR')];
  database.businessDemoSeed = compact.businessDemoSeed;
  return true;
}